import { FC, useContext, useMemo, useState } from 'react';

import {
  BonusIcon,
  H1,
  H2,
  InfoBlock,
  Preloader,
  Tooltip as TooltipKit,
} from '@smartshell/ui-kit';
import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { ReportSummaryBlock, ReportSummaryTooltip } from 'components';
import { useWindowResize } from 'hooks';
import { LocalizationContext } from 'localization';
import { SettingsContext } from 'settings';

import { RevenueTooltip } from '../Revenue/RevenueTooltip';
import { formatMoney, useSummaryValues } from '../utils';
import s from './Bonuses.module.scss';
import { LABEL_TRANSLATION_MAP } from './constants';
import { IBonusesProps, IChartDataItem } from './interfaces';

export const Bonuses: FC<IBonusesProps> = ({
  data,
  labels,
  values,
  loading,
  sum,
}) => {
  const { t } = useContext(LocalizationContext);
  const {
    state: {
      settings: { currency },
    },
  } = useContext(SettingsContext);

  const { getValue, getExtraValue } = useSummaryValues(data, loading);
  const size = useWindowResize();

  const normalizedData = [
    {
      text: t('visitors.bonus.without.returns'),
      value: `${formatMoney(getValue('total_bonus'))}`,
      icon: <BonusIcon color="secondary" />,
      percent: !loading ? `${getExtraValue('total_bonus_with_refunds')}%` : '-',
      tooltip: (
        <ReportSummaryTooltip
          title={t('visitors.bonus.without.returns')}
          text_1={t('bonus.sum')}
          value_1={`${formatMoney(getValue('total_bonus'))}`}
          text_2={t('percentage.total.revenue')}
          value_2={`${getExtraValue('total_bonus_with_refunds')}%`}
          footer={t('visitors.bonus.tooltip')}
          variant="primary"
        />
      ),
    },
    {
      text: t('visitors.bonus.returns'),
      value:
        typeof getValue('total_bonus') === 'number'
          ? formatMoney(
              Number(
                (
                  Number(getValue('total_bonus')) -
                  Number(getValue('total_bonus_with_refunds'))
                ).toFixed(2)
              )
            )
          : '—',

      icon: <BonusIcon color="secondary" />,
    },
  ];

  const getChartData = (
    labels: string[] | undefined,
    values: number[] | undefined
  ): IChartDataItem[] => {
    if (!labels || !values) return [];

    return labels
      .map((label, index) => {
        if (label.includes('_percent')) return null;

        const key = label as keyof typeof LABEL_TRANSLATION_MAP;
        const translationKey = LABEL_TRANSLATION_MAP[key];

        if (!translationKey) return null;

        return {
          name: t(translationKey),
          value: values[index] || 0,
        };
      })
      .filter((item): item is { name: string; value: number } => !!item)
      .filter((item) => item.value !== 0);
  };

  const chartData = useMemo(
    () => getChartData(labels, values?.values),
    [labels, values, t]
  );

  const [focusBar, setFocusBar] = useState<number | null>(null);

  return (
    <div className={s.container}>
      <H1>{t('money.bonus')}</H1>
      <div className={s.summary_block}>
        {normalizedData?.map((item) => (
          <ReportSummaryBlock key={item.text} item={item} />
        ))}
      </div>
      {size.width > 960 ? (
        <div className={s.content}>
          <div className={s.header}>
            <H2 className={s.title}>{t('visitors.bonus.distribution')}</H2>
            <TooltipKit
              contentClassName={s.tooltip}
              content={t('visitors.bonus.table.tooltip')}
            />
          </div>
          {(labels === undefined || values?.values.every((val) => val === 0)) &&
          !loading ? (
            <div className={s.placeholder}>
              {values?.values.every((val) => val === 0)
                ? t('visitors.empty.data')
                : t('report.placeholder.emptyDates')}
            </div>
          ) : (
            <Preloader loading={loading}>
              <ResponsiveContainer
                className={s.data}
                width="100%"
                height="100%"
              >
                <BarChart
                  data={chartData}
                  margin={{
                    top: 30,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                  onMouseMove={(state) => {
                    if (state.isTooltipActive) {
                      setFocusBar(state.activeTooltipIndex ?? null);
                    } else {
                      setFocusBar(null);
                    }
                  }}
                >
                  <XAxis
                    dataKey="name"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: 'var(--text-primary)' }}
                    fontSize={12}
                  />
                  <YAxis
                    tickFormatter={(value) => `${value} ${currency.sign}`}
                    axisLine={false}
                    width={130}
                    dx={-55}
                    tickLine={false}
                    tick={{
                      fill: 'var(--text-primary)',
                      style: { whiteSpace: 'nowrap' },
                    }}
                    fontSize={12}
                  />
                  <Tooltip
                    cursor={{ fill: 'var(--bg-item-secondary)' }}
                    content={({ payload }) => {
                      const rawValue = payload?.[0]?.value;
                      const value = typeof rawValue === 'number' ? rawValue : 0;
                      const label = payload?.[0]?.payload?.name;

                      const percentage =
                        !loading && typeof sum === 'number' && sum > 0
                          ? `${((value / sum) * 100).toFixed(2)}%`
                          : '-';

                      return (
                        <RevenueTooltip
                          title={String(label)}
                          text_1={t('bonus.sum')}
                          value_1={`${formatMoney(value)}`}
                          text_2={t('visitors.bonuses.tooltip.percent')}
                          value_2={percentage}
                          variant="primary"
                        />
                      );
                    }}
                  />

                  <Bar
                    fill="var(--bg-blue-10)"
                    dataKey="value"
                    radius={[4, 4, 0, 0]}
                    isAnimationActive={false}
                  >
                    <LabelList
                      content={(props) => {
                        const { x, y, width, value } = props;
                        const percent =
                          typeof sum === 'number' && sum > 0
                            ? `${((Number(value) / sum) * 100).toFixed(0)}%`
                            : '';
                        const XPosition = Number(x) + Number(width) / 2;
                        const YPosition = Number(y) - 8;

                        return (
                          <text
                            x={XPosition}
                            y={YPosition}
                            fill="var(--text-primary)"
                            fontSize="18"
                            textAnchor="middle"
                          >
                            {percent}
                          </text>
                        );
                      }}
                    />
                    {chartData?.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={
                          focusBar === index
                            ? 'var(--bg-blue)'
                            : 'var(--bg-blue-10)'
                        }
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Preloader>
          )}
        </div>
      ) : (
        <InfoBlock
          className={s.mobile_info}
          title={t('mobile.placeholder')}
          iconName="ComputerIcon"
          color="blue"
        />
      )}
    </div>
  );
};
